import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { captionPlacement, useSoftSpotlight } from '../lib/tourMotion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const seenKey = id => `aceit_tutorial_seen:${id || 'guest'}`;
export function shouldShowTutorial(id) { try { return !localStorage.getItem(seenKey(id)); } catch { return true; } }
export function markTutorialSeen(id) { try { localStorage.setItem(seenKey(id), 'true'); } catch {} }
export const TOUR_STEPS = [
  { id: 'welcome', route: '/', target: '.ace-welcome', title: 'Your learning home', text: 'Start a tutor session here. Your dashboard missions suggest useful next steps.' },
  { id: 'syllabus', route: '/syllabus', target: '[data-testid^="subject-btn-"], [data-tour="syllabus"] h1', title: 'Choose your chapter', text: 'Choose a subject, then a chapter from your course. Lessons and practice stay connected to that chapter.' },
  { id: 'tutor', route: '/chat', target: '[data-tour="tutor-response"] .message-ai, [data-testid="chat-input"]', title: 'Watch the tutor answer', text: 'A demo chat opens, sends a question and receives a sample explanation. Your own chats answer questions about your selected chapter.' },
  { id: 'visuals', route: '/chat', target: '[data-tour="visual-example"]', title: 'See your chapter visually', text: 'Visualize turns chapter ideas into mind maps, graphs, revision notes and downloadable cheat sheets. Here is a sample mind map.' },
  { id: 'quiz', route: '/quiz', target: '[data-testid="quiz-subject-select"], [data-tour="quiz-workspace"] h1', title: 'Practise what you learned', text: 'Choose your chapter and difficulty, then generate a quiz. Completed answers help identify what needs revision.' },
  { id: 'progress', route: '/progress', target: '[data-tour="study-focus"]', title: 'Focus on the next step', text: 'See your top priorities here. Revise opens the chapter tutor; Practise prepares a topic quiz. Expand a topic to see the evidence.' },
  { id: 'appearance', route: '/settings', target: '[aria-labelledby="appearance-title"]', title: 'Choose your look', text: 'Switch between Bright and Classic here. You can replay this walkthrough from Settings whenever you need it.' },
];

export default function NewUserTutorial({ onClose }) {
  const { user, setUser } = useAuth();
  const nav = useNavigate(), location = useLocation();
  const origin = useRef({ pathname: location.pathname, state: location.state });
  const originalFocus = useRef(document.activeElement);
  const originalReserve = useRef(document.documentElement.style.getPropertyValue('--ace-tour-reserve'));
  const [step, setStep] = useState(0);
  const [targetRect, setRect] = useState(null);
  const [reduced, setReduced] = useState(() => matchMedia('(prefers-reduced-motion: reduce)').matches);
  const animatedRect = useSoftSpotlight(targetRect, reduced);
  const rect = animatedRect?.step === step ? animatedRect : null;
  const [panelSize, setPanelSize] = useState({ width: Math.min(340, innerWidth - 28), height: 190 });
  const [placement, setPlacement] = useState(() => ({ ...captionPlacement(null, { width: innerWidth, height: innerHeight }, { width: Math.min(340, innerWidth - 28), height: 190 }), docked: innerWidth < 760 }));
  const docked = useRef(innerWidth < 760);
  const sizeRef = useRef(panelSize);
  const [moving, setMoving] = useState(false);
  const [waiting, setWaiting] = useState(true);
  const panel = useRef(null);
  const current = TOUR_STEPS[step];
  const close = useCallback(() => {
    markTutorialSeen(user?.user_id);
    setUser?.(previous => ({ ...previous, is_tutorial_seen: true }));
    axios.patch(`${API}/auth/tutorial/seen`, {}, { withCredentials: true }).catch(() => {});
    nav(origin.current.pathname, { replace: true, state: origin.current.state });
    onClose?.();
  }, [user?.user_id, setUser, nav, onClose]);
  useEffect(() => {
    const chat = current.id === 'tutor' || current.id === 'visuals';
    nav(current.route, { replace: true, state: chat ? { tutorial_demo: true, tutorial_visuals: current.id === 'visuals' } : null });
    docked.current = innerWidth < 760;
    setRect(null); setWaiting(true); setMoving(true);
    const timer = setTimeout(() => setMoving(false), reduced ? 0 : 380);
    return () => clearTimeout(timer);
  }, [current.route, step, nav]);

  useEffect(() => {
    const media = matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduced(media.matches);
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);
  useEffect(() => {
    document.documentElement.dataset.aceTourStep = current.id;
    document.documentElement.style.setProperty('--ace-tour-reserve', placement.docked ? `${Math.ceil(panelSize.height) + 28}px` : '0px');
  }, [placement.docked, panelSize.height, current.id]);
  // Only dock when there is no clear space beside the target (or on mobile).
  useEffect(() => {
    const html = document.documentElement, app = document.querySelector('.App');
    const previousInert = app?.inert, previousTour = html.dataset.aceTour;
    const oldReserve = originalReserve.current;
    html.dataset.aceTour = 'active';
    if (app) app.inert = true;
    const measure = () => {
      if (!panel.current) return;
      const box = panel.current.getBoundingClientRect();
      const next = { width: box.width, height: box.height };
      sizeRef.current = next;
      setPanelSize(previous => Math.abs(previous.height - next.height) < 1 && Math.abs(previous.width - next.width) < 1 ? previous : next);
    };
    const observer = new ResizeObserver(measure);
    if (panel.current) observer.observe(panel.current);
    measure();
    return () => {
      observer.disconnect();
      delete html.dataset.aceTourStep;
      if (previousTour === undefined) delete html.dataset.aceTour; else html.dataset.aceTour = previousTour;
      if (oldReserve) html.style.setProperty('--ace-tour-reserve', oldReserve); else html.style.removeProperty('--ace-tour-reserve');
      if (app) app.inert = previousInert;
      if (originalFocus.current?.isConnected) originalFocus.current.focus?.();
    };
  }, []);
  useEffect(() => {
    let element = null, frame = null, settleScroll = null;
    const update = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const candidates = current.target.split(',').flatMap(selector => [...document.querySelectorAll(selector.trim())]);
        const found = candidates.find(node => node.getBoundingClientRect().width > 0 && node.getBoundingClientRect().height > 0);
        if (!found) return;
        const shell = document.querySelector('.ace-app-shell');
        if (element !== found) {
          if (element) resize.unobserve(element);
          element = found; resize.observe(found);
          found.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
          clearTimeout(settleScroll);
          settleScroll = setTimeout(() => { if (found.isConnected) { found.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' }); update(); } }, 450);
        }
        const box = found.getBoundingClientRect();
        const radius = Math.min(44, Math.max(12, parseFloat(getComputedStyle(found).borderTopLeftRadius) + 9 || 18));
        const raw = { x: Math.max(8, box.left - 9), y: Math.max(8, box.top - 9), right: Math.min(innerWidth - 8, box.right + 9), bottom: Math.min(innerHeight - 8, box.bottom + 9), radius };
        const besideVisual = current.id === 'visuals' && innerWidth >= 1100;
        const nextPlacement = besideVisual
          ? { x: innerWidth - sizeRef.current.width - 14, y: Math.max(14, (innerHeight - sizeRef.current.height) / 2), docked: false }
          : captionPlacement(raw, { width: innerWidth, height: innerHeight }, sizeRef.current, docked.current);
        if (nextPlacement.docked) docked.current = true;
        setPlacement(previous => Math.abs(previous.x - nextPlacement.x) < 1 && Math.abs(previous.y - nextPlacement.y) < 1 && previous.docked === nextPlacement.docked ? previous : nextPlacement);
        const limit = Math.min(shell?.getBoundingClientRect().bottom || innerHeight, nextPlacement.docked ? nextPlacement.y - 16 : innerHeight - 8);
        const next = { ...raw, step, bottom: Math.min(limit, raw.bottom) };
        if (next.bottom <= next.y || next.right <= next.x) return;
        setRect(previous => previous && Object.keys(next).every(key => Math.abs(previous[key] - next[key]) < 1) ? previous : next);
        setWaiting(false);
      });
    };
    const resize = new ResizeObserver(update), observer = new MutationObserver(update);
    const shell = document.querySelector('.ace-app-shell');
    observer.observe(shell || document.body, { childList: true, subtree: true });
    if (shell) resize.observe(shell);
    const settle = setTimeout(update, 400), timeout = setTimeout(() => setWaiting(false), 6500);
    window.addEventListener('resize', update); document.addEventListener('scroll', update, true);
    update(); panel.current?.focus();
    return () => { if (frame) cancelAnimationFrame(frame); clearTimeout(settle); clearTimeout(timeout); clearTimeout(settleScroll); resize.disconnect(); observer.disconnect(); window.removeEventListener('resize', update); document.removeEventListener('scroll', update, true); };
  }, [current.target, step, location.pathname, panelSize.height, reduced]);
  const keyboard = event => {
    if (event.key === 'Escape') { event.preventDefault(); close(); }
    if (event.key !== 'Tab') return;
    const buttons = [...panel.current.querySelectorAll('button')].filter(node => !node.disabled);
    const first = buttons[0], last = buttons[buttons.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || document.activeElement === panel.current)) { event.preventDefault(); first?.focus(); }
  };
  const spotWidth = rect ? Math.max(0, rect.right - rect.x) : 0;
  const spotHeight = rect ? Math.max(0, rect.bottom - rect.y) : 0;
  const radius = rect ? Math.min(rect.radius, spotWidth / 2, spotHeight / 2) : 0;
  return createPortal(<div className="ace-tour-overlay" data-testid="new-user-tutorial" onKeyDown={keyboard}>
    <svg className="ace-tour-mask" width="100%" height="100%" aria-hidden="true" style={{ opacity: waiting ? .65 : 1 }}>
      <defs><mask id="ace-tour-cutout"><rect width="100%" height="100%" fill="white" />{rect && <rect x={rect.x} y={rect.y} width={spotWidth} height={spotHeight} rx={radius} fill="black" className="ace-tour-aperture" style={{ opacity: moving || waiting ? 0 : 1 }} />}</mask></defs>
      <rect className="ace-tour-veil" width="100%" height="100%" mask="url(#ace-tour-cutout)" />
      {rect && <rect data-testid="tour-spotlight" className="ace-tour-spotlight" style={{ opacity: moving || waiting ? 0 : 1 }} x={rect.x} y={rect.y} width={spotWidth} height={spotHeight} rx={radius} />}
    </svg>
    <section ref={panel} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="tour-title" aria-describedby="tour-description" className="ace-tour-panel ace-tour-caption" data-docked={placement.docked} data-transitioning={moving || waiting} style={{ transform: `translate3d(${placement.x}px, ${placement.y}px, 0)` }}>
      <div className="ace-tour-panel-top"><span>{step + 1} / {TOUR_STEPS.length}</span><button type="button" aria-label="Close tutorial" data-testid="tutorial-skip-btn" onClick={close}>×</button></div>
      <div className="ace-tour-copy" key={current.id}><h2 id="tour-title">{current.title}</h2><p id="tour-description">{current.text}</p></div>
      {waiting && <span role="status" className="ace-tour-loading">Opening section…</span>}
      {!waiting && !rect && <span role="status" className="ace-tour-loading">This section is still loading. You can continue and return later.</span>}
      <div className="ace-tour-actions"><button type="button" disabled={step === 0 || moving || waiting} onClick={() => setStep(value => value - 1)}>Back</button><button type="button" onClick={close}>Skip</button><button type="button" data-testid="tutorial-next-btn" disabled={moving || waiting} onClick={() => step === TOUR_STEPS.length - 1 ? close() : setStep(value => value + 1)}>{step === TOUR_STEPS.length - 1 ? 'Finish' : 'Next'}</button></div>
    </section>
  </div>, document.body);
}

